import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, SpinLoading, Toast } from 'antd-mobile';
import { CheckCircleOutline, CloseCircleOutline } from 'antd-mobile-icons';
import { bindWorkerByQRCode } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './WorkerQRRedirect.css';

export default function WorkerQRRedirect() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, initialized, isAuthenticated } = useAuth();
  const [binding, setBinding] = useState(false);
  const [bindResult, setBindResult] = useState(null); // { success: true/false, message: string }
  const hasProcessedRef = useRef(false); // 防止重复处理

  useEffect(() => {
    // 防止重复处理
    if (hasProcessedRef.current) {
      return;
    }

    // 等待认证状态初始化
    if (!initialized) {
      return;
    }

    // 检查二维码参数
    const code = searchParams.get('code');
    if (!code) {
      hasProcessedRef.current = true;
      Toast.show({
        icon: 'fail',
        content: 'Invalid staff QR code link',
      });
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 2000);
      return;
    }

    // 如果未登录，保存 workerCode 并跳转到登录页
    if (!isAuthenticated || !user?.id) {
      hasProcessedRef.current = true;
      // 保存 workerCode 到 localStorage，登录后可以继续绑定
      localStorage.setItem('pending-worker-code', code);
      Toast.show({
        icon: 'fail',
        content: 'Please sign in first',
      });
      setTimeout(() => {
        // 跳转到登录页，登录成功后会自动回到绑定流程
        navigate(`/login?redirect=/worker?code=${encodeURIComponent(code)}`, { replace: true });
      }, 1500);
      return;
    }

    // 如果已经绑定过，不再重复绑定
    if (binding || bindResult) {
      return;
    }

    // 标记为已处理，防止重复执行
    hasProcessedRef.current = true;

    // 自动执行绑定
    const handleBind = async () => {
      setBinding(true);
      try {
        await bindWorkerByQRCode(code, user.id);
        setBindResult({
          success: true,
          message: 'Customer service representative linked successfully',
        });
        Toast.show({
          icon: 'success',
          content: 'Customer service representative linked successfully',
        });

        // 清除待处理的 workerCode
        localStorage.removeItem('pending-worker-code');

        // 3秒后跳转到首页
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 3000);
      } catch (error) {
        setBindResult({
          success: false,
          message: error.message || 'Linking failed. Please try again.',
        });
        Toast.show({
          icon: 'fail',
          content: error.message || 'Linking failed. Please try again.',
        });

        // 3秒后跳转到首页
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 3000);
      } finally {
        setBinding(false);
      }
    };

    handleBind();
  }, [searchParams, initialized, isAuthenticated, user, navigate, binding, bindResult]);

  // 显示加载状态
  if (!initialized || binding) {
    return (
      <div className="worker-qr-redirect-page">
        <div className="worker-qr-container">
          <SpinLoading color="primary" style={{ fontSize: '48px' }} />
          <div className="worker-qr-loading-text">Linking customer service representative...</div>
        </div>
      </div>
    );
  }

  // 显示绑定结果
  if (bindResult) {
    return (
      <div className="worker-qr-redirect-page">
        <div className="worker-qr-container">
          <Card className="worker-qr-result-card">
            {bindResult.success ? (
              <>
                <CheckCircleOutline
                  style={{ fontSize: '64px', color: '#52c41a', marginBottom: '16px' }}
                />
                <div className="worker-qr-result-title">Linked successfully</div>
                <div className="worker-qr-result-message">{bindResult.message}</div>
              </>
            ) : (
              <>
                <CloseCircleOutline
                  style={{ fontSize: '64px', color: '#ff4d4f', marginBottom: '16px' }}
                />
                <div className="worker-qr-result-title">Linking failed</div>
                <div className="worker-qr-result-message">{bindResult.message}</div>
              </>
            )}
            <Button
              block
              color="primary"
              onClick={() => navigate('/', { replace: true })}
              style={{ marginTop: '24px' }}
            >
              Back to Home
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  // 默认状态（不应该到达这里，但为了安全起见）
  return (
    <div className="worker-qr-redirect-page">
      <div className="worker-qr-container">
        <SpinLoading color="primary" style={{ fontSize: '48px' }} />
        <div className="worker-qr-loading-text">Processing...</div>
      </div>
    </div>
  );
}
